import {useParams, useSearchParams} from "react-router-dom";
import QueryErrorAlert from "../../../components/QueryErrorAlert";
import React, {useEffect} from "react";
import {useTranslation} from "react-i18next";
import Page, {ContentTitle} from "../../../components/Layout/Page";
import TabSection from "../../../components/Layout/Page/TabSection";
import {TerminationGeneralTab} from "./TerminationGeneralTab";
import {TerminationField, TerminationStatus} from "../../../app/api/model/termination";
import {TerminationCommunicationTab} from "./TerminationCommunicationTab";
import {Alert, Box, Grid, Stack, Typography} from "@mui/material";
import {getMailIcon} from "../helpers";
import {formatTimestamp} from "../../../utils/datetime";
import {useTerminationCompleteMutation, useTerminationGetQuery} from "../../../app/api/api";
import {useModal} from "../../../hooks/useModal";
import ConfirmDialog from "../../../components/Dialog/ConfirmDialog";
import {useSnackbar} from "notistack";

export default function TerminationDetails() {
    const {t} = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();
    const params = useParams();
    const terminationId = parseInt(params.terminationId ?? "");
    const {data: termination, error} = useTerminationGetQuery(terminationId);
    const [completeTermination] = useTerminationCompleteMutation();
    const completeDialogModal = useModal(
        termination?.[TerminationField.STATUS] === TerminationStatus.PENDING_COMPLETION
    );
    useEffect(() => {
        completeDialogModal.setIsOpen(termination?.[TerminationField.STATUS] === TerminationStatus.PENDING_COMPLETION);
    }, [termination?.[TerminationField.STATUS]]);
    const {enqueueSnackbar} = useSnackbar();
    const handleCompleteTermination = async () => {
        if (terminationId) {
            return completeTermination({
                id: terminationId,
            })
                .unwrap()
                .then(() => {
                    enqueueSnackbar(t("generic_completedSuccessfully"), {variant: "success"});
                    completeDialogModal.setIsOpen(false);
                });
        }
    };

    if (error) {
        return <QueryErrorAlert error={error} />;
    }

    const resetSearchParams = () => {
        if (searchParams.toString() !== "") {
            setSearchParams("");
        }
    };

    return (
        <Page>
            <ContentTitle
                titleKey="generic_termination"
                subtitle={termination?.[TerminationField.CUSTOMER_NAME]}
                entry={termination}
                toolEntries={[
                    {
                        translationKey: "generic_customerNumber",
                        fieldName: TerminationField.CUSTOMER_NUMBER,
                    },
                    {
                        translationKey: "termination_terminationAt",
                        fieldName: TerminationField.TERMINATION_AT,
                    },
                ]}
            />
            {termination?.[TerminationField.MAIL_PENDING] && (
                <Alert severity="warning" sx={{margin: "-30px 15px 15px 15px"}}>
                    <Stack direction="row">
                        <Box sx={{margin: "auto 0"}}>{getMailIcon(termination[TerminationField.MAIL_PENDING])}</Box>
                        <Box sx={{margin: "auto 0 auto 10px"}}>
                            {t("termination_mailPendingAtWithValues", {
                                type: termination[TerminationField.MAIL_PENDING],
                                time: formatTimestamp(termination[TerminationField.MAIL_AT]),
                            })}
                        </Box>
                    </Stack>
                </Alert>
            )}
            <TabSection
                title={t("generic_termination")}
                tabs={[
                    {
                        label: t("tab_general"),
                        content: termination && <TerminationGeneralTab termination={termination} />,
                    },
                    {
                        label: t("tab_communication"),
                        content: <TerminationCommunicationTab terminationId={terminationId} />,
                    },
                ]}
                onChange={resetSearchParams}
            />
            <ConfirmDialog
                title={t("title_completeTermination")}
                titleConfirm={t("action_complete")}
                isOpen={completeDialogModal.isOpen}
                onConfirm={handleCompleteTermination}
                onClose={completeDialogModal.onClose}
            >
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Typography variant={"body1"}>{t("body_completeTermination")}</Typography>
                    </Grid>
                </Grid>
            </ConfirmDialog>
        </Page>
    );
}
